import React from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  VStack,
  HStack,
  Image,
  Tooltip,
  Spinner,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Badge,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { TbPlayerPlayFilled } from "react-icons/tb";
import { useAutoSyncController } from "./useAutoSyncController";
import { AddOrEditAutoSyncConfiguration } from "./components/AddOrEditAutoSyncConfiguration";
import { Brokers } from "../../types";
import { useNavigate } from "react-router";
import { FaCog, FaSync } from "react-icons/fa";
import { InfoIcon, WarningTwoIcon } from "@chakra-ui/icons";
import DeleteSyncSettingsComponent from "./components/DeleteSyncSettingsComponent";
import moment from "moment";

export const AutoSync = ({ minimalGui }: { minimalGui?: boolean }) => {
  const controller = useAutoSyncController();
  const navigate = useNavigate();
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const getPanelContent = () => {
    if (controller.settings.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <FaSync size={24} opacity={0.5} style={{ margin: "0 auto 8px" }} />
          <Text fontSize="sm" mb={2}>
            No sync settings configured
          </Text>
         
        </Box>
      );
    } else {
      return (
        <VStack w="full" spacing={2} maxH="240px" overflowY="auto">
          {controller.settings.map((setting) => {
            // Check if setting has an 'active' property and provide a fallback
            const isActive = 'active' in setting ? setting.active : true;
            
            // Helper function to get image source
            const getImageSource = (): string => {
              if (setting.broker === Brokers.Schwab) {
                return "https://cdn.wealthbee.io/main/brokers/broker_icons/schwab.png";
              } else if (setting.broker === Brokers.ETrade) {
                return "https://cdn.wealthbee.io/main/brokers/broker_icons/etrade.png";
              } else if (setting.broker === Brokers.IBKR) {
                return "https://cdn.wealthbee.io/main/brokers/broker_icons/interactive-brokers.png";
              } else if (setting.broker === Brokers.SnapTrade) {
                return "https://cdn.wealthbee.io/main/brokers/logo/snaptrade.png";
              }
              return "";
            };
            
            return (
              <Box 
                key={setting.syncSettingId}
                w="100%" 
                p={3} 
                borderWidth="1px" 
                borderColor={borderColor}
                borderRadius="md"
                bg={useColorModeValue("white", "gray.800")}
                _hover={{ borderColor: "blue.300" }}
                transition="border-color 0.2s"
              >
                <HStack justifyContent="space-between" w="100%">
                  <HStack spacing={3} flex={1}>
                    <Box position="relative">
                      <Image
                        height="20px"
                        width="20px"
                        src={getImageSource()}
                        borderRadius="sm"
                        alt={`${setting.broker} icon`}
                      />
                      {!isActive && (
                        <Box
                          position="absolute"
                          top="-2px"
                          right="-2px"
                          bg="red.500"
                          borderRadius="full"
                          w="8px"
                          h="8px"
                        />
                      )}
                    </Box>
                    
                    <Flex direction="column" flex={1}>
                      <HStack>
                        <Text fontWeight="medium" fontSize="sm">{setting.broker}</Text>
                        <Badge 
                          colorScheme={isActive ? "green" : "red"} 
                          size="sm"
                        >
                          {isActive ? "Active" : "Inactive"}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {moment(setting.updatedAt).fromNow()}
                      </Text>
                    </Flex>
                  </HStack>

                  <HStack spacing={1}>
                    <Button
                      size="xs"
                      isDisabled={!isActive}
                      leftIcon={<TbPlayerPlayFilled size={12} />}
                      colorScheme={isActive ? "green" : "gray"}
                      onClick={() => controller.run(setting)}
                    >
                      Sync
                    </Button>
                    
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        if (minimalGui) {
                          navigate("/app/settings");
                        } else {
                          controller.setSelectedSettings(setting);
                          controller.setShowConfigure(true);
                        }
                      }}
                    >
                      <FaCog size={12} />
                    </Button>
                     <DeleteSyncSettingsComponent
                       setting={setting}
                       controller={controller}
                     />
                  </HStack>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      );
    }
  };

  if (minimalGui) {
    return (
      <Flex p={1}>
        {controller.loading && (
          <Flex
            bg="var(--bg)"
            h="18px"
            w="40px"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size="sm" />
          </Flex>
        )}

        <Popover placement="bottom-start">
          <PopoverTrigger>
            <Button
              px={2}
              py={1}
              m={0}
              bg={useColorModeValue("white", "gray.800")}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              boxShadow="sm"
              _hover={{
                boxShadow: "md",
                borderColor: "blue.300",
                transform: "translateY(-1px)",
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: "sm",
              }}
              transition="all 0.2s ease"
              minH="32px"
              h="32px"
            >
              {!controller.loading && controller.settings.length === 0 && (
                <Flex align="center" gap={1}>
                  <FaSync size={14} opacity={0.6} />
                  <Text fontSize="xs" color="gray.500">
                    Auto Sync
                  </Text>
                </Flex>
              )}
              {!controller.loading && controller.settings.length > 0 && (
                <HStack spacing={1}>
                  {controller.settings.map((setting, index) => {
                    // Check if setting has an 'active' property and provide a fallback
                    const isActive = 'active' in setting ? Boolean(setting.active) : true;
                    
                    // Helper function to get image source
                    const getImageSource = (): string => {
                      if (setting.broker === Brokers.Schwab) {
                        return "https://cdn.wealthbee.io/main/brokers/broker_icons/schwab.png";
                      } else if (setting.broker === Brokers.ETrade) {
                        return "https://cdn.wealthbee.io/main/brokers/broker_icons/etrade.png";
                      } else if (setting.broker === Brokers.IBKR) {
                        return "https://cdn.wealthbee.io/main/brokers/broker_icons/interactive-brokers.png";
                      } else if (setting.broker === Brokers.SnapTrade) {
                        return "https://cdn.wealthbee.io/main/brokers/logo/snaptrade.png";
                      }
                      return "";
                    };
                    
                    return (
                      <Box key={setting.syncSettingId} position="relative">
                        {!isActive && (
                          <Tooltip
                            label={`${setting.broker} is not connected. Please activate it in the settings page`}
                          >
                            <Box
                              position="absolute"
                              top="-2px"
                              right="-2px"
                              w="10px"
                              h="10px"
                              bg="red.500"
                              borderRadius="full"
                              border="2px solid"
                              borderColor={useColorModeValue("white", "gray.800")}
                              zIndex={1}
                            />
                          </Tooltip>
                        )}
                        <Box
                          p={0.5}
                          borderRadius="sm"
                          bg={isActive ? "green.50" : "red.50"}
                          borderWidth="1px"
                          borderColor={isActive ? "green.200" : "red.200"}
                          _dark={{
                            bg: isActive ? "green.900" : "red.900",
                            borderColor: isActive ? "green.700" : "red.700",
                          }}
                        >
                          <Image
                            height="18px"
                            width="18px"
                            src={getImageSource()}
                            borderRadius="sm"
                            alt={`${setting.broker} icon`}
                            opacity={isActive ? 1 : 0.7}
                          />
                        </Box>
                        {index < controller.settings.length - 1 && (
                          <Box
                            position="absolute"
                            right="-4px"
                            top="50%"
                            transform="translateY(-50%)"
                            w="2px"
                            h="2px"
                            bg="gray.300"
                            borderRadius="full"
                          />
                        )}
                      </Box>
                    );
                  })}
                </HStack>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent w="360px" bg="var(--bg)" boxShadow="lg" borderColor={borderColor}>
            <PopoverArrow bg="var(--bg)" />
            <PopoverBody p={3}>
              <HStack justifyContent="space-between" mb={3}>
                <Text fontWeight="semibold" fontSize="sm">Auto Sync Settings</Text>
                <Button
                  size="xs"
                  variant="ghost"
                  rightIcon={<FaCog size={10} />}
                  onClick={() => navigate("/app/settings")}
                  fontSize="xs"
                >
                  Manage All
                </Button>
              </HStack>
              <Box>{getPanelContent()}</Box>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h3" size="lg">
          Auto Sync
        </Heading>
        <Tooltip label="Automatically sync your investment data from supported brokers">
          <InfoIcon ml={2} color="gray.500" />
        </Tooltip>
      </Flex>
      
      {controller.loading ? (
        <Flex justify="center" py={8}>
          <Spinner size="lg" />
        </Flex>
      ) : (
        getPanelContent()
      )}
      
      <Flex mt={6}>
        <Button
          w="full"
          colorScheme="blue"
          leftIcon={<FaCog />}
          onClick={() => {
            controller.setSelectedSettings(undefined);
            controller.setShowConfigure(true);
          }}
          size="lg"
          bg="var(--accent)"
          color="white"
          _hover={{ bg: "var(--accent)", opacity: 0.9 }}
        >
          Add New Configuration
        </Button>
      </Flex>
      
      <Modal
        closeOnOverlayClick={false}
        isOpen={controller.showConfigure}
        onClose={() => controller.setShowConfigure(false)}
        scrollBehavior="inside"
        isCentered
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent bg="var(--bg)">
          <ModalHeader>Configure Auto Sync</ModalHeader>
          <ModalCloseButton size="sm" />
          <ModalBody>
            <AddOrEditAutoSyncConfiguration
              settings={controller.selectedSettings}
              close={() => controller.setShowConfigure(false)}
            />
          </ModalBody>
          <Box h="10px" />
        </ModalContent>
      </Modal>
    </Flex>
  );
};
